import type { Camp } from '@/types/database'
import type { CampData } from '@/store/reducer'

export interface AiChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface GeneratedItinerary {
  camp: {
    name: string
    destination: string
    pax_adult: number
    pax_child: number
    budget_total: number
    ai_summary: string
  }
  days: Array<{
    day_number: number
    theme: string
    activities: Array<{
      time: string
      type: 'transport' | 'accommodation' | 'content' | 'meal' | 'free'
      description: string
      note?: string
    }>
  }>
}

export function serializeCampContext(camp: Camp, campData: CampData, activeTab?: string): string {
  const lines: string[] = [
    `## 案件情報`,
    `- 名前: ${camp.name}`,
    `- ステータス: ${camp.status}`,
    `- 行き先: ${camp.destination ?? '未定'}`,
    `- 日程: ${camp.start_date ?? '未定'} 〜 ${camp.end_date ?? '未定'}`,
    `- 参加者: 大人${camp.pax_adult}名 子供${camp.pax_child}名`,
    `- 予算: ${camp.budget_total.toLocaleString()}円`,
    `- 進捗: ${camp.progress}%`,
  ]

  if (campData.itineraryDays.length > 0) {
    lines.push('', '## 旅程')
    for (const day of campData.itineraryDays) {
      lines.push(`### Day ${day.day_number}: ${day.theme ?? ''}`)
      const dayActivities = campData.activities
        .filter((a) => a.itinerary_day_id === day.id)
        .sort((a, b) => a.sort_order - b.sort_order)
      for (const act of dayActivities) {
        lines.push(`  - ${act.time ?? ''} [${act.type}] ${act.description}`)
      }
    }
  }

  if (campData.participants.length > 0) {
    lines.push('', `## 参加者: ${campData.participants.length}名`)
  }

  if (campData.quotes.length > 0) {
    const latestQuote = [...campData.quotes].sort((a, b) => b.version - a.version)[0]
    lines.push('', `## 見積もり: v${latestQuote.version} (${latestQuote.status}) - ${latestQuote.total.toLocaleString()}円`)
  }

  if (activeTab) {
    lines.push('', `## 現在のタブ: ${activeTab}`)
  }

  return lines.join('\n')
}

export async function sendChatMessage(
  messages: AiChatMessage[],
  systemPrompt: string,
): Promise<string> {
  const res = await fetch('/api/ai/chat', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ messages, systemPrompt }),
  })
  const data = (await res.json().catch(() => null)) as { message?: string; error?: string } | null
  if (!res.ok || data?.error) throw new Error(data?.error ?? `AI chat request failed (${res.status})`)
  return data?.message ?? ''
}

export async function generateItinerary(prompt: string): Promise<GeneratedItinerary> {
  const res = await fetch('/api/ai/itinerary', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ prompt }),
  })
  const data = (await res.json().catch(() => null)) as (GeneratedItinerary & { error?: string }) | null
  if (!res.ok || data?.error) throw new Error(data?.error ?? `Itinerary generation failed (${res.status})`)
  return data as GeneratedItinerary
}
